import { createSlice, Draft, PayloadAction } from "@reduxjs/toolkit";

import type { UploadedMedia } from "@/client/media-upload";
import { getCurrentDate } from "@/utils/datetime";

/**
 * Where an Upload task stands. There is no `done`: a task that completes leaves
 * the queue, so every status the queue holds is an unfinished one.
 */
export type UploadTaskStatus = "queued" | "uploading" | "retrying" | "failed";

export interface UploadTask {
  turnId: string;
  dialogueId: string;
  answerBlockId: string;
  workId: string;

  /** What `completeSocraticTurn` needs. Captured when recording stopped. */
  durationSeconds: number;
  recordingStartedAt: string | undefined;
  recordingCompletedAt: string;

  status: UploadTaskStatus;
  attempts: number;
  /** The student recorded this answer block again. Set when the replacement is queued. */
  superseded: boolean;

  /** Set once the media is in S3. A retry from here calls the mutation only. */
  media?: UploadedMedia;

  /** Percent and when it last moved. Drives the bar and the stall backstop. */
  progress?: { percent: number; at: string };
}

/** What is known when the recording stops. The reducer owns the lifecycle fields. */
export type QueuedUpload = Pick<
  UploadTask,
  | "turnId"
  | "dialogueId"
  | "answerBlockId"
  | "workId"
  | "durationSeconds"
  | "recordingStartedAt"
  | "recordingCompletedAt"
>;

export interface UploadQueueState {
  /** FIFO: oldest first, one upload at a time. */
  tasks: UploadTask[];
}

const initialState: UploadQueueState = {
  tasks: [],
};

/** Find in-flight attempt. */
function findRunningTask(
  state: Draft<UploadQueueState>,
  turnId: string
): Draft<UploadTask> | undefined {
  const task = state.tasks.find((t) => t.turnId === turnId);
  if (!task) return undefined;
  if (task.status !== "uploading" && task.status !== "retrying") {
    return undefined;
  }
  return task;
}

export const uploadQueueSlice = createSlice({
  name: "uploadQueue",
  initialState,
  reducers: {
    /* A recording is ready to upload. */
    uploadQueued: (state, action: PayloadAction<QueuedUpload>) => {
      for (const existing of state.tasks) {
        if (existing.answerBlockId === action.payload.answerBlockId) {
          existing.superseded = true;
        }
      }

      state.tasks.push({
        ...action.payload,
        status: "queued",
        attempts: 0,
        superseded: false,
      });
    },

    uploadStarted: (state, action: PayloadAction<string>) => {
      const task = state.tasks.find((t) => t.turnId === action.payload);
      if (!task) return;

      task.status = "uploading";
      task.attempts += 1;
      task.progress = { percent: 0, at: getCurrentDate().toISOString() };
    },

    uploadProgress: (
      state,
      action: PayloadAction<{ turnId: string; percent: number }>
    ) => {
      const task = findRunningTask(state, action.payload.turnId);
      if (!task) return;

      task.progress = {
        percent: action.payload.percent,
        at: getCurrentDate().toISOString(),
      };
    },

    /** After media is uploaded, we reset the number of attempts */
    mediaUploaded: (
      state,
      action: PayloadAction<{ turnId: string; media: UploadedMedia }>
    ) => {
      const task = findRunningTask(state, action.payload.turnId);
      if (!task) return;

      task.media = action.payload.media;
      task.attempts = 0;
    },

    uploadRetryScheduled: (state, action: PayloadAction<string>) => {
      const task = findRunningTask(state, action.payload);
      if (!task) return;

      task.status = "retrying";
    },

    uploadFailed: (state, action: PayloadAction<string>) => {
      const task = findRunningTask(state, action.payload);
      if (!task) return;

      task.status = "failed";
    },

    turnCompleted: (state, action: PayloadAction<string>) => {
      const task = findRunningTask(state, action.payload);
      if (!task) return;

      state.tasks = state.tasks.filter((t) => t.turnId !== action.payload);
    },

    /* A superseded attempt is given up on. */
    uploadDropped: (state, action: PayloadAction<string>) => {
      state.tasks = state.tasks.filter((t) => t.turnId !== action.payload);
    },

    /**
     * The attempt starts over from zero, so any recorded progress is no longer
     * true and the retry budget resets.
     */
    uploadRestarted: (state, action: PayloadAction<string>) => {
      const task = state.tasks.find((t) => t.turnId === action.payload);
      if (!task) return;

      task.status = "queued";
      task.progress = undefined;
      task.attempts = 0;
    },
  },
});

export const {
  uploadQueued,
  uploadStarted,
  uploadProgress,
  mediaUploaded,
  uploadRetryScheduled,
  uploadFailed,
  turnCompleted,
  uploadDropped,
  uploadRestarted,
} = uploadQueueSlice.actions;

export const uploadQueueReducer = uploadQueueSlice.reducer;
